import React from 'react';
import styled from 'styled-components';

const ControlsContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 25px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Section = styled.div`
  margin-bottom: 25px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: white;
  margin: 0 0 15px 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const ControlGroup = styled.div`
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  color: white;
  margin-bottom: 8px;
  font-size: 0.9rem;
  font-weight: 500;
`;

const Slider = styled.input`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.2);
  outline: none;
  -webkit-appearance: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #4ecdc4;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  }
  
  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #4ecdc4;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  }
`;

const ValueDisplay = styled.span`
  color: #4ecdc4;
  font-weight: bold;
  font-size: 0.9rem;
`;

const ButtonGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const Button = styled.button`
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: capitalize;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: #4ecdc4;
  color: white;
  
  &:hover:not(:disabled) {
    background: #45b7d1;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(78, 205, 196, 0.4);
  }
`;

const SecondaryButton = styled(Button)`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
  }
`;

const AlgorithmButton = styled(Button)`
  background: ${props => props.isActive ? '#4ecdc4' : 'rgba(255, 255, 255, 0.2)'};
  color: ${props => props.isActive ? 'white' : 'white'};
  border: 1px solid ${props => props.isActive ? '#4ecdc4' : 'rgba(255, 255, 255, 0.3)'};
  
  &:hover:not(:disabled) {
    background: ${props => props.isActive ? '#45b7d1' : 'rgba(255, 255, 255, 0.3)'};
    transform: translateY(-2px);
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  color: #4ecdc4;
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  color: white;
  font-size: 0.8rem;
  opacity: 0.8;
`;

const Controls = ({
  arraySize,
  setArraySize,
  sortingSpeed,
  setSortingSpeed,
  onGenerateArray,
  onSort,
  isSorting,
  currentAlgorithm,
  comparisons,
  swaps
}) => {
  const algorithms = [
    { key: 'bubble', name: 'Bubble Sort' },
    { key: 'selection', name: 'Selection Sort' },
    { key: 'insertion', name: 'Insertion Sort' },
    { key: 'merge', name: 'Merge Sort' },
    { key: 'quick', name: 'Quick Sort' }
  ];

  return (
    <ControlsContainer>
      <Section>
        <SectionTitle>Array Settings</SectionTitle>
        <ControlGroup>
          <Label>
            Array Size: <ValueDisplay>{arraySize}</ValueDisplay>
          </Label>
          <Slider
            type="range"
            min="5"
            max="50"
            value={arraySize}
            onChange={(e) => setArraySize(parseInt(e.target.value))}
            disabled={isSorting}
          />
        </ControlGroup>
        
        <ControlGroup>
          <Label>
            Speed: <ValueDisplay>{sortingSpeed}ms</ValueDisplay>
          </Label>
          <Slider
            type="range"
            min="10"
            max="200"
            value={sortingSpeed}
            onChange={(e) => setSortingSpeed(parseInt(e.target.value))}
            disabled={isSorting}
          />
        </ControlGroup>
        
        <SecondaryButton
          onClick={onGenerateArray}
          disabled={isSorting}
        >
          Generate New Array
        </SecondaryButton>
        
        {isSorting && (
          <div style={{ 
            textAlign: 'center', 
            marginTop: '15px', 
            padding: '10px', 
            background: 'rgba(78, 205, 196, 0.2)', 
            borderRadius: '8px',
            color: 'white',
            fontSize: '0.9rem'
          }}>
            🚀 Running {currentAlgorithm.charAt(0).toUpperCase() + currentAlgorithm.slice(1)} Sort...
          </div>
        )}
      </Section>

      <Section>
        <SectionTitle>Sorting Algorithms</SectionTitle>
        <ButtonGrid>
          {algorithms.map((algo) => (
            <AlgorithmButton
              key={algo.key}
              isActive={false}
              onClick={() => onSort(algo.key)}
              disabled={isSorting}
            >
              {algo.name}
            </AlgorithmButton>
          ))}
        </ButtonGrid>
      </Section>

      <Stats>
        <StatItem>
          <StatValue>{comparisons}</StatValue>
          <StatLabel>Comparisons</StatLabel>
        </StatItem>
        <StatItem>
          <StatValue>{swaps}</StatValue>
          <StatLabel>Swaps</StatLabel>
        </StatItem>
      </Stats>
    </ControlsContainer>
  );
};

export default Controls;
